import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RRule, rrulestr } from 'rrule';

dayjs.extend(utc);
dayjs.extend(timezone);

const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';

/** The instant's wall-clock time in `timeZone`, as a floating UTC date for rrule. */
const toWallClock = (instant: string, timeZone: string): Date =>
  dayjs.utc(dayjs(instant).tz(timeZone).format(WALL_CLOCK_FORMAT)).toDate();

const fromWallClock = (wallClock: Date, timeZone: string): string =>
  dayjs.tz(dayjs.utc(wallClock).format(WALL_CLOCK_FORMAT), timeZone).toISOString();

/**
 * Next occurrence of `recurrence` (anchored at `dtstart`) strictly after `after`.
 *
 * Occurrences keep the series' wall-clock time in `timeZone`, the way the calendar and the
 * backend expand schedules, so a series started before a DST change continues at the same
 * local time after it.
 */
export const getNextRecurrenceStartTime = ({
  recurrence,
  dtstart,
  after,
  timeZone,
}: {
  recurrence: string;
  dtstart: string;
  after: string;
  timeZone: string;
}): string | null => {
  const { until, ...options } = rrulestr(recurrence).origOptions;
  const rule = new RRule({ ...options, dtstart: toWallClock(dtstart, timeZone) });
  const nextDate = rule.after(toWallClock(after, timeZone), false);

  if (!nextDate) return null;

  const next = fromWallClock(nextDate, timeZone);
  // UNTIL is a real instant, so it is compared outside the wall-clock expansion.
  return until && dayjs(next).isAfter(until) ? null : next;
};

/**
 * Reconstruct the edited occurrence's original instant: the edited day (from the
 * possibly-retimed form value) at the series' original local time of day. Anchoring the
 * continuation lookup on this — rather than the new form time — stops an
 * earlier-in-the-day edit from pulling the next occurrence back onto the edited
 * day.
 */
export const getEditedOccurrenceStartTime = (
  initialEventStartTime: string,
  editedStartTime: string,
  timeZone: string,
): string => {
  const initial = dayjs(initialEventStartTime).tz(timeZone);
  const editedDay = dayjs(editedStartTime).tz(timeZone).format('YYYY-MM-DD');

  return dayjs.tz(`${editedDay}T${initial.format('HH:mm:ss.SSS')}`, timeZone).toISOString();
};
